import { useEffect } from 'react';
import { Card, Col, Form, Row } from 'antd';
import { isBefore, isSameDay, parseISO } from 'date-fns';

import FormDatePicker from '@/views/components/Inputs/FormDatePicker';
import FormInput from '@/views/components/Inputs/FormInput';
import FormRadioGroup from '@/views/components/Inputs/FormRadioGroup';
import FormSelectBox from '@/views/components/Inputs/FormSelectBox';
import HiddenFormInput from '@/views/components/Inputs/HiddenFormInput';
import SecondaryLabel from '@/views/components/Labels/SecondaryLabel';
import ToggleSwitch from '@/views/components/Switch/ToggleSwitch';
import { STATUS_OPTIONS } from '@/views/pages/Savers/TrailerManagement/constants';

import TMSFiledCard from '../../common/components/TMSFiledCard';
import TrailerDrawer from './TrailerDrawer';

const TrailerDetailsForm = ({
  hidden,
  closeModal,
  formNewTrailer,
  handleAddTrailer,
  onTrailerNumberInput,
  unitRentalCompanyIdOptions,
  unitSizeIdOptions,
  statusOptions,
  checkOptions,
  handleOptionToggle,
  trailerFormMode,
  trailerNumberOptions,
  trailerLocationIdOptions,
  searchStatusId,
  setSearchStatusId,
  rentalStatus,
  handleRentalStatusToggle,
}) => {
  const onSelectFilterByStatus = (e) => {
    setSearchStatusId(e.value);
  };

  const rentalStartDate = Form.useWatch('rentalTrailerDate', formNewTrailer);

  // biome-ignore lint/correctness/useExhaustiveDependencies:Fix this
  useEffect(() => {
    if (trailerFormMode === 'add') {
      formNewTrailer.setFieldsValue({
        status: 'EMT',
      });
    }
  }, [formNewTrailer, trailerFormMode, hidden]);

  useEffect(() => {
    if (rentalStartDate) {
      formNewTrailer.validateFields(['inspectionExpDate']);
    }
  }, [rentalStartDate, formNewTrailer]);
  return (
    <TrailerDrawer
      openTrailerDrawer={hidden}
      closeTrailerDrawer={closeModal}
      trailerForm={formNewTrailer}
      drawerHeader={trailerFormMode === 'add' ? 'Add Trailer' : 'Update Trailer'}
      submitForm={handleAddTrailer}
    >
      <div className="trailer-form">
        {/* Basic Information Section */}
        <Card size="small" className="mb-4" title="Basic Information">
          <Row gutter={20}>
            <Col sm={12}>
              {trailerFormMode === 'add' ? (
                <FormInput
                  name="trailerNumber"
                  label="Trailer #"
                  margin="m-0"
                  editMode
                  maxLength="100"
                  rules={[
                    {
                      message: 'Trailer number required',
                      required: true,
                    },
                  ]}
                />
              ) : (
                <FormSelectBox
                  label="Trailer #"
                  editMode
                  name="trailerNumber"
                  margin="m-0"
                  placeholder="Trailer #"
                  options={trailerNumberOptions}
                  onChange={onTrailerNumberInput}
                  rules={[
                    {
                      message: 'Trailer number required',
                      required: true,
                    },
                  ]}
                />
              )}
            </Col>

            <Col sm={12}>
              <FormSelectBox
                label="Trailer Location"
                editMode
                name="locationID"
                margin="m-0"
                placeholder="Trailer Location"
                options={trailerLocationIdOptions}
                rules={[
                  {
                    message: 'Trailer location required',
                    required: true,
                  },
                ]}
              />
            </Col>
          </Row>

          <Row gutter={20}>
            <Col sm={12}>
              <FormSelectBox
                label="Unit Rental Company"
                editMode
                name="unitRentalCompanyID"
                margin="m-0"
                placeholder="Unit Rental Company"
                options={unitRentalCompanyIdOptions}
                rules={[
                  {
                    message: 'Unit rental company required',
                    required: true,
                  },
                ]}
              />
            </Col>
            <Col sm={12}>
              <FormSelectBox
                label="Unit Size Id"
                editMode
                name="unitSizeID"
                margin="m-0"
                placeholder="Unit Size Id"
                options={unitSizeIdOptions}
                rules={[
                  {
                    message: 'Unit size ID required',
                    required: true,
                  },
                ]}
              />
            </Col>
          </Row>

          <Row gutter={20}>
            <Col sm={12}>
              <FormSelectBox
                label="Status"
                editMode
                name="status"
                margin="m-0"
                placeholder="Status"
                value={searchStatusId}
                onChange={onSelectFilterByStatus}
                options={STATUS_OPTIONS}
                disabled={trailerFormMode === 'add'}
                rules={[
                  {
                    message: 'Status required',
                    required: true,
                  },
                ]}
              />
            </Col>
            <Col sm={12}>
              <FormDatePicker
                name="rentalTrailerDate"
                label="Rental Start Date"
                placeholder="Rental Start Date"
                disabledTargetDate=""
                rules={[
                  {
                    message: 'Rental start date required',
                    required: true,
                  },
                ]}
              />
            </Col>
          </Row>
          <Row gutter={20} className="pl-4">
            <Col sm={12}>
              <SecondaryLabel className="f-14"> Roll Door?: </SecondaryLabel>
              <FormRadioGroup
                options={[
                  {
                    label: 'Yes',
                    value: true,
                  },
                  {
                    label: 'No',
                    value: false,
                  },
                ]}
                name="rollDoor"
                label="Roll Door"
                value={checkOptions.rollDoor}
                onChange={handleOptionToggle}
              />
            </Col>
            <Col sm={12}>
              <FormDatePicker
                name="inspectionExpDate"
                label="Inspection Expiry Date"
                placeholder="Inspection Expiry Date"
                disabledTargetDate=""
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      const rentalStartDate = formNewTrailer.getFieldValue('rentalTrailerDate');
                      if (!rentalStartDate) {
                        return Promise.resolve();
                      }
                      // Convert values to Date objects
                      const inspectionDate =
                        value instanceof Date ? value : typeof value === 'string' ? parseISO(value) : new Date(value);
                      const rentalDate =
                        rentalStartDate instanceof Date
                          ? rentalStartDate
                          : typeof rentalStartDate === 'string'
                            ? parseISO(rentalStartDate)
                            : new Date(rentalStartDate);

                      if (isBefore(inspectionDate, rentalDate) || isSameDay(inspectionDate, rentalDate)) {
                        return Promise.reject(
                          new Error('Inspection Expiry Date must be later than Rental Start Date.'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Col>
          </Row>

          {trailerFormMode !== 'add' && searchStatusId === 'EMT' && (
            <Row gutter={20}>
              <Col sm={12} className="mt-10">
                <TMSFiledCard containerClass="mb-10" title="Rental Status">
                  <ToggleSwitch
                    checkedChildren="ON Rent"
                    unCheckedChildren="OFF Rent"
                    onClick={handleRentalStatusToggle}
                    checked={Boolean(rentalStatus)}
                  />
                </TMSFiledCard>
              </Col>
              <Col sm={12}>
                <FormDatePicker
                  name="rentalRemovalDate"
                  label="Rental Removal Date"
                  placeholder="Rental Removal Date"
                  disabledTargetDate=""
                />
              </Col>
            </Row>
          )}
        </Card>

        {/* Submit Button */}
        <div className="mt-6">
          <button type="submit" className="btn btn-dipblue cursor-pointer">
            {trailerFormMode === 'add' ? 'Create Trailer' : 'Update Trailer'}
          </button>
        </div>
        <HiddenFormInput name="rentalStatus" />
      </div>
    </TrailerDrawer>
  );
};

export default TrailerDetailsForm;
